import { ErrandResponse } from '@backend/schemas/src/errands'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useServiceGroups } from '@/api/hooks/useServiceGroups'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useIndividuals } from '@/hooks/useIndividuals'
import { ErrandServiceGroup, getErrandServiceGroups } from '@/utils/errand-services.utils'

type Props = {
  errand: ErrandResponse
}

type SelectedService = ErrandServiceGroup['chosenServices'][number]

/**
 * Which support(s) an errand concerns — sits directly on the page background (no card), since
 * it's meant to read like part of the page, not a separate widget. Each area+service pair is one
 * compact "Area - Service" link (à la Hitta stöd's "Läs mer på ..." links) rather than a heading
 * per area with a list underneath — tighter, and an accordion would mean tall buttons and a lot
 * of scrolling for something the user just needs to glance at or occasionally read more about.
 * Clicking a service opens its info in a dialog instead.
 */
export const ErrandServicesSection: FC<Props> = ({ errand }) => {
  const { t } = useTranslation()
  const { selectedIndividualId } = useIndividuals()
  const { data: allServiceGroups = [] } = useServiceGroups(selectedIndividualId)
  const [selectedService, setSelectedService] = useState<SelectedService | null>(null)

  const errandServiceGroups = getErrandServiceGroups(errand, allServiceGroups)

  const rows = errandServiceGroups.flatMap(group =>
    group.chosenServices.map(service => ({ group, id: service.id, service })),
  )

  if (rows.length === 0) return null

  return (
    <div className="flex flex-col gap-3">
      <h4>{t('errandsPage.info.areasLabel')}</h4>
      <div className="flex flex-col items-start">
        {rows.map(({ group, id, service }) => (
          <Button key={id} onClick={() => setSelectedService(service)} size="inline" variant="link">
            {group.title} - {service.title}
          </Button>
        ))}
      </div>

      <Dialog onOpenChange={open => !open && setSelectedService(null)} open={!!selectedService}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedService?.title}</DialogTitle>
          </DialogHeader>
          <p className="max-w-prose whitespace-pre-wrap">{selectedService?.description}</p>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default ErrandServicesSection
